// ./src/utils/api/AddonCategoryService.ts
//
// Per-event add-on categories ("T-shirt size", "Lunch option") — groups add-ons
// so a host can build several add-ons under one category. Mirrors the
// eventsmagic-api EventAddonCategory SDL. `where.id` accepts one id or many (a
// bulk update >1 id may only change status, server-enforced).
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';

const FIELDS = `
  id
  eventId
  name
  description
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query EventAddonCategoryList($filter: EventAddonCategoryFilter, $params: PaginationAndSorting) {
    eventAddonCategoryList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation EventAddonCategoryCreate($params: EventAddonCategoryInput) {
    eventAddonCategoryCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation EventAddonCategoryUpdate($where: EventAddonCategoryWhereInput, $params: EventAddonCategoryInput) {
    eventAddonCategoryUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation EventAddonCategoryRemove($where: EventAddonCategoryWhereInput) {
    eventAddonCategoryRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface AddonCategory {
  id?: string;
  eventId?: string | null;
  name?: string | null;
  description?: string | null;
  orderNo?: number | null;
  status?: number | null;
  rights?: string[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface AddonCategoryInput {
  eventId?: string | null;
  name?: string | null;
  description?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

export class AddonCategoryService {
  static async list(eventId: string, statuses?: number[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: { eventId: [eventId], status: statuses },
    });
  }

  static async create(params: AddonCategoryInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  /** `id` may be a single id or an array (bulk — status-only). */
  static async update(id: string | string[], params: AddonCategoryInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}
